import React, { useState, useEffect } from "react";
import { MdSchool } from "react-icons/md";
import NiveauServiceInstance from "service/niveau.service";
import ActionBar from "components/action-bar";
import NiveauTable from "./components/NiveauTable";
import NiveauFormModal from "./components/NiveauFormModal";
import DeleteConfirmationModal from "./components/DeleteConfirmationModal";
import HeaderSection from "components/headers-section";

const NiveauManager = () => {
  const [niveaux, setNiveaux] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentNiveau, setCurrentNiveau] = useState({
    name_niveau: ""
  });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchNiveaux();
  }, []);

  const fetchNiveaux = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await NiveauServiceInstance.getAll();
      setNiveaux(result || []);
    } catch (error) {
      setError(
        "Impossible de charger les niveaux. Veuillez réessayer plus tard."
      );
      console.error("Error fetching niveaux:", error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setCurrentNiveau({
      name_niveau: ""
    });
    setIsEditing(false);
    setIsFormModalOpen(true);
  };

  const openEditModal = (niveau) => {
    setCurrentNiveau({
      id_niveau: niveau.id_niveau,
      name_niveau: niveau.name_niveau,
    });
    setIsEditing(true);
    setIsFormModalOpen(true);
  };


  const openDeleteModal = (niveau) => {
    setCurrentNiveau(niveau);
    setIsDeleteModalOpen(true);
  };

  const handleSubmitForm = async (formData) => {
    setLoading(true);
    try {
      if (isEditing) {
        await NiveauServiceInstance.update(
          currentNiveau.id_niveau,
          formData
        );
      } else {
        await NiveauServiceInstance.create(formData);
      }
      
      fetchNiveaux();
      setIsFormModalOpen(false);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        errors: error.response?.data?.errors || {
          general: isEditing 
            ? "Impossible de modifier le niveau. Veuillez réessayer."
            : "Impossible de créer le niveau. Veuillez réessayer."
        }
      };
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await NiveauServiceInstance.delete(currentNiveau.id_niveau);
      fetchNiveaux();
      setIsDeleteModalOpen(false);
    } catch (error) {
      setError("Impossible de supprimer le niveau. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const filteredNiveaux = niveaux.filter(
    (niveau) => niveau.name_niveau.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="px-5 py-8 md:px-8 bg-gray-50 min-h-screen">

        <HeaderSection
          title="Gestion des Niveaux"
          description="Créez et gérez les niveaux d'études"
          accentColor="#57A863"
        />

        <ActionBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Rechercher un niveau..."
          searchIcon={MdSchool}
          onRefresh={fetchNiveaux}
          onCreateNew={openCreateModal}
          buttonLabel="Nouveau Niveau"
          loading={loading}
          primaryColor="#57A863"
          hoverColor="#4c9657"
        />

        {error && (
          <div
            className="mb-6 rounded-lg bg-red-50 p-4 text-sm text-red-600 border border-red-200"
            role="alert"
          >
            {error}
          </div>
        )}

        <NiveauTable
          niveaux={filteredNiveaux}
          loading={loading}
          onEdit={openEditModal}
          onDelete={openDeleteModal}
        />

      <NiveauFormModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        onSubmit={handleSubmitForm}
        initialNiveau={currentNiveau}
        isEditing={isEditing}
        existingNiveaux={niveaux}
        loading={loading}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        item={currentNiveau}
        itemName={currentNiveau.name_niveau}
        itemType="niveau"
        warning="La suppression de ce niveau peut affecter les étudiants qui y sont associés."
        loading={loading}
      />
    </div>
  );
};

export default NiveauManager;